import React from 'react';
import { Modal, View, Image, Pressable, TouchableOpacity, Text, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { CommonActions } from '@react-navigation/native';
import { Icon as RNIcon } from 'react-native-elements';
import OrderModalResponseProps from 'types/components/OrderModalResponseProps';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import ShoppingBarSuccess from 'assets/images/shopping-bar-success.svg';
import ShoppingBarCardError from 'assets/images/shopping-bar-card-error.svg';
import { styles } from 'styles/components/orderResponseModal';
import OrderImageType from 'types/enums/OrderImageType';

export default ({
  success,
  message,
  orderNumber,
  orderImage,
  verificationCode,
  onClose,
  navigation,
  isOrderPayWithCloverDevice,
  redirectToPayment,
}: OrderModalResponseProps): JSX.Element => {
  const { currentVenue, cloverConnected, pairedWithClover } = React.useContext(AppContext);
  const { currentCommerce } = React.useContext(OrderContext);
  const { t } = useTranslation();

  const redirectToOrdersScreen = () => {
    onClose();
    navigation.dispatch({
      ...CommonActions.reset({
        index: 1,
        routes: [
          {
            name: 'Home',
            state: {
              routes: [
                {
                  name: 'HomeScreen',
                },
              ],
            },
          },
          {
            name: 'Orders',
            state: {
              routes: [
                {
                  name: 'OrdersScreen',
                  params: Platform.OS === 'web' ? { venueCode: currentVenue?.code } : undefined,
                },
              ],
            },
          },
        ],
      }),
    });
  };

  const returnToRetryScreen = () => {
    onClose();
    if (redirectToPayment) {
      redirectToPayment();
      return;
    }
    navigation.reset({
      index: 1,
      routes: [
        { name: cloverConnected ? 'CommerceScreen' : 'HomeScreen' },
        {
          name: 'ShoppingCartScreen',
          params:
            Platform.OS === 'web'
              ? {
                  venueCode: currentVenue?.code,
                  commerceCode: currentCommerce?.code,
                }
              : undefined,
        },
      ],
    });
  };

  return (
    <Modal animationType="slide" transparent={true} visible={true} style={styles.modal}>
      <View style={styles.container}>
        <View style={styles.responseDataContainer}>
          <View style={styles.closeButton}>
            <Pressable
              onPress={() => {
                if (success) {
                  onClose();
                } else {
                  returnToRetryScreen();
                }
              }}
              style={styles.closeOpacity}
            >
              <RNIcon
                name="close"
                style={[styles.closeImage, pairedWithClover && { height: 100, width: 100 }]}
                size={pairedWithClover ? 100 : 25}
              />
            </Pressable>
          </View>
          <View style={[styles.modalImageContainer, { backgroundColor: success ? '#00A80E' : '#E50303' }]}>
            <Image
              source={success ? ShoppingBarSuccess : ShoppingBarCardError}
              style={[styles.modalImage, { width: success ? 50 : 60 }, success && { height: 62 }]}
            />
          </View>
          <View style={styles.generalTextContainer}>
            <Text
              style={[
                styles.title,
                pairedWithClover && { fontSize: 36, lineHeight: 40 },
                { color: success ? '#00A80E' : '#E50303' },
              ]}
            >
              {success ? t('order-response-title-success') : t('order-response-title-payment-problem')}
            </Text>
            {success ? (
              <View style={styles.successDataContainer}>
                <Text style={[styles.orderNumberTitle, pairedWithClover && { fontSize: 48, lineHeight: 40 }]}>
                  {t('order-response-number')}{' '}
                </Text>
                <Text style={[styles.orderNumberText, pairedWithClover && { fontSize: 48, lineHeight: 40 }]}>
                  {orderNumber}
                </Text>
                <Text style={[styles.emailMessage, pairedWithClover && { fontSize: 36, lineHeight: 40 }]}>{`${t(
                  'order-response-suggestion-mail',
                )} ${`${
                  message ? t('order-response-messsage-no-clover-payment') : t('order-response-message-clover-payment')
                }`}`}</Text>
                <Text style={[styles.verificationCode, pairedWithClover && { fontSize: 36, lineHeight: 40 }]}>{`${t(
                  'order-response-verification-code',
                )}: ${verificationCode}`}</Text>
                <Text style={[styles.deliveryMessage, pairedWithClover && { fontSize: 36, lineHeight: 40 }]}>{`${t(
                  'order-response-advice-ticket',
                )} ${`${
                  message
                    ? t('order-response-advice-ticket-no-clover-payment')
                    : t('order-response-advice-ticket-on-clover-payment')
                }`}`}</Text>
                {success && orderImage && (
                  <Image
                    source={{ uri: orderImage.path }}
                    style={{
                      width: orderImage.orderImageTypeId === OrderImageType.Qr ? 160 : 200,
                      height: orderImage.orderImageTypeId === OrderImageType.Qr ? 160 : 100,
                    }}
                  />
                )}
              </View>
            ) : (
              <View style={styles.errorContainer}>
                <Text style={[styles.errorTitle, pairedWithClover && { fontSize: 36, lineHeight: 40 }]}>
                  {message ? message : <Text>{t('order-response-error')}.</Text>}
                </Text>
                <Text style={[styles.errorAdvice, pairedWithClover && { fontSize: 36, lineHeight: 40 }]}>
                  {t('order-response-advice-retry-cash-payment')}
                </Text>
              </View>
            )}
            {!success && (
              <TouchableOpacity
                style={[styles.retryButtonContainer, { backgroundColor: currentVenue?.appColor ?? '#E50303' }]}
                onPress={() => {
                  returnToRetryScreen();
                }}
              >
                <View style={styles.retryTextContainer}>
                  <Text style={styles.retryText}>{t('retry-payment')}</Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
          {!isOrderPayWithCloverDevice && (
            <View style={styles.goToOrdersContainer}>
              <Text style={styles.goToOrdersText} onPress={redirectToOrdersScreen}>
                {t('go-to-orders')}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};
